import React, { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { useForm } from 'react-hook-form';
import Select from 'react-select';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { ITableConfig } from 'src/components/common/table';
import { ODataFilter, ODataOptions, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';

interface IClientsBilledReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
    // When set, this instance is one of the Renewable/Western Land quick-access entries -
    // dialogTitle/fixedReportType/defaultFilename come from the specific REPORT_TYPE_OPTIONS entry
    // (see ReportDashboardPage.tsx), the Report Type <select> is hidden, and reportType is sent as
    // this value instead of whatever the (absent) select would have held. Omit for the general
    // "Clients Billed" entry, which keeps the full Report Type dropdown.
    dialogTitle?: string;
    fixedReportType?: string;
    defaultFilename?: string;
}

interface IPeriodOptionType {
    value: number;
    label: string;
}

// marginRight is explicit here (not left to RHFInputBox's own default) because RHFSelect's
// labelOnLeft path does NOT add one on its own - without it, Report Type's <select> sits flush
// against its label while the other three rows (which do get a margin, one way or another) don't,
// throwing off the left edge alignment of all four fields.
const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '10rem', textAlign: 'left', marginRight: '1rem' };
// Report Type/Filename need a narrower 9rem label (vs. Beginning/Ending Period's 10rem) to keep
// their fields aligned with the Period Selects now that all the fields are only 65% wide.
const reportTypeFilenameLabelStyle: React.CSSProperties = { ...labelStyle, width: '9rem' };
const compactSelectStyles = {
    control: (provided: React.CSSProperties) => ({ ...provided, minHeight: '3rem', height: '3rem' }),
    valueContainer: (provided: React.CSSProperties) => ({ ...provided, height: '3rem', padding: '0 8px' }),
    input: (provided: React.CSSProperties) => ({ ...provided, margin: '0', padding: '0' }),
    indicatorsContainer: (provided: React.CSSProperties) => ({ ...provided, height: '3rem' }),
    menu: (provided: React.CSSProperties) => ({ ...provided, zIndex: 999 }),
};

// Labels/values confirmed directly off clients_billed.fmt's own REPORT_TYPE poplist (TP=28/29/30) -
// each selects an entirely separate report server entry (see
// ReportService.GetClientsBilledReportFile), not a value passed into one shared proc.
const REPORT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'STD', label: 'Standard Clients Billed' },
    { value: 'REN', label: 'Renewables Clients Billed' },
    { value: 'WL', label: 'Western Land Clients Billed' },
];

// Backs the Accounts Receivable "Clients Billed" report. Unlike Invoice Breakdown By County/
// Land-Renewables Revenue, its underlying procs (usp_ClientsBilled_Summary/
// usp_ClientsBilledRenew_Detail/usp_ClientsBilledWestLand_Detail) take BegPeriod|EndPeriod as period
// numbers (TRY_CONVERT(INT, ...)), not dates - despite Oracle's own popup labeling the fields
// "Beginning/Ending Date", the fields are relabeled "Period" here and use the same debounced
// period-search Select PayPeriodRangeDialog.tsx/CommissionSearch.tsx already use, matching what the
// backend actually expects. Filename mirrors Oracle's own FILENAME field - purely a client-side
// download name, not sent to the report server.
const ClientsBilledReportDialog = (props: IClientsBilledReportDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { get: getReport, response: reportResponse } = useFetch();
    const { get: getPayPeriods } = useFetch();
    const [isWorking, setIsWorking] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [periodOptions, setPeriodOptions] = useState<Array<IPeriodOptionType>>([]);
    const [selectedBegPeriod, setSelectedBegPeriod] = useState<IPeriodOptionType | null>(null);
    const [selectedEndPeriod, setSelectedEndPeriod] = useState<IPeriodOptionType | null>(null);

    const insertDash = (periodNumber: number) => {
        const str = periodNumber.toString();
        const insertIndex = str.length - 2;
        return str.slice(0, insertIndex) + '-' + str.slice(insertIndex);
    };

    const normalizePeriodInput = (str: string) => str.replace(/-/g, '');

    const loadPeriodOptions = async (searchTerm: string) => {
        const tableConfig: ITableConfig = { rowCount: 100, idField: 'Id', pageNumber: 1, columns: [] };
        const options = ODataOptions.from(tableConfig);
        options.clearFilters();
        options.addPaging(1, 50);
        options.addOrderBy('Id', ODataOrderByDirection.Descending);

        const numericTerm = Number(normalizePeriodInput(searchTerm || ''));
        if (searchTerm && !isNaN(numericTerm)) {
            options.addFilter(new ODataFilter('PERIOD_NO', ODataFilterOperators.Contains, numericTerm));
        } else {
            const now = new Date();
            const begDate = new Date(new Date().setMonth(now.getMonth() - 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            const endDate = new Date(new Date().setMonth(now.getMonth() + 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            options.addFilter(new ODataFilter('end_date', ODataFilterOperators.LessThan, endDate));
            options.addFilter(new ODataFilter('beg_date', ODataFilterOperators.GreaterThan, begDate));
        }

        const result = await getPayPeriods(`/v1.0/AccountsPayable/payperiods${options.toString()}`);
        if (Array.isArray(result?.items)) {
            setPeriodOptions(result.items.map((pp: { periodNumber: number }) => ({
                value: pp.periodNumber,
                label: insertDash(pp.periodNumber),
            })));
        }
    };

    const periodSearch = _.debounce((inputText: string) => loadPeriodOptions(inputText), 300);

    useEffect(() => {
        loadPeriodOptions('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const generateReport = async () => {
        const values = getValues();
        if (!selectedBegPeriod?.value || !selectedEndPeriod?.value) {
            setMessage('A beginning and ending period must be entered.');
            return;
        }

        setMessage('');
        setIsWorking(true);
        const params = new URLSearchParams({
            reportType: props.fixedReportType || values.reportType || 'STD',
            begPeriod: selectedBegPeriod.value.toString(),
            endPeriod: selectedEndPeriod.value.toString(),
        });
        await getReport(`/v1.0/reports/GetReport/ClientsBilled?${params.toString()}`);
        setIsWorking(false);

        if (!reportResponse.ok) {
            setMessage('Unable to generate the report. Please try again.');
            return;
        }

        if (reportResponse.status === 204) {
            setMessage('No clients matched those filters.');
            return;
        }

        const blob = await reportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        const defaultName = props.defaultFilename || 'ClientsBilled';
        const baseName = (values.filename || defaultName).trim().replace(/\.xlsx$/i, '');
        a.download = `${baseName || defaultName}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        props.hideDialog();
    };

    return (
        <Dialog title={props.dialogTitle || 'Clients Billed'} dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '28%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '85%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '.5rem' }}>
                        <label style={{ ...labelStyle, flexShrink: 0 }}>Beginning Period</label>
                        <div style={{ width: '65%' }}>
                            <Select
                                placeholder="Period#"
                                options={periodOptions}
                                value={selectedBegPeriod}
                                onChange={(option: IPeriodOptionType | null) => setSelectedBegPeriod(option)}
                                onInputChange={(inputText: string) => periodSearch(inputText)}
                                filterOption={(option, inputValue) => (
                                    normalizePeriodInput(option.label).includes(normalizePeriodInput(inputValue))
                                )}
                                isClearable={true}
                                isSearchable={true}
                                styles={compactSelectStyles}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '.5rem' }}>
                        <label style={{ ...labelStyle, flexShrink: 0 }}>Ending Period</label>
                        <div style={{ width: '65%' }}>
                            <Select
                                placeholder="Period#"
                                options={periodOptions}
                                value={selectedEndPeriod}
                                onChange={(option: IPeriodOptionType | null) => setSelectedEndPeriod(option)}
                                onInputChange={(inputText: string) => periodSearch(inputText)}
                                filterOption={(option, inputValue) => (
                                    normalizePeriodInput(option.label).includes(normalizePeriodInput(inputValue))
                                )}
                                isClearable={true}
                                isSearchable={true}
                                styles={compactSelectStyles}
                            />
                        </div>
                    </div>
                    {!props.fixedReportType && (
                        <RHFSelect
                            label="Report Type"
                            labelOnLeft={true}
                            labelStyle={reportTypeFilenameLabelStyle}
                            name="reportType"
                            defaultVal="STD"
                            options={REPORT_TYPE_OPTIONS}
                            register={register}
                            style={{ width: '65%' }}
                            bodyStyle={{ width: '100%' }}
                        />
                    )}
                    <RHFInputBox
                        label="Filename"
                        labelOnLeft={true}
                        labelStyle={reportTypeFilenameLabelStyle}
                        name="filename"
                        type="text"
                        defaultVal={props.defaultFilename || 'ClientsBilled'}
                        register={register}
                        errors={errors}
                        style={{ width: '65%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={isWorking ? 'Generating...' : 'Generate'}
                    loading={isWorking}
                    onClick={() => generateReport()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text="Cancel"
                    onClick={() => props.hideDialog()}
                />
            </div>
            {!!message && (
                <div style={{ color: 'red', textAlign: 'center', marginTop: '0.5rem' }}>{message}</div>
            )}
        </Dialog>
    );
};

export default ClientsBilledReportDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/dialogs/ClientsBilledReportDialog.tsx